import logger from './logger';

// `window.axios` is the authenticated instance set up in entrypoints/dashboard.js
// (carries devise-token-auth headers), same pattern as the ai_assist feature.
const http = () => window.axios;

const base = (accountId, conversationId) =>
  `/api/v1/accounts/${accountId}/conversations/${conversationId}/counsel`;

// Read-only snapshot of the counsel thread. Returns
// { thread: null | { id, conversation_id, created_at, messages: [] },
//   auto_reply_enabled, bot_bound, auto_reply_busy,
//   counsel_busy, counsel_user_id, counsel_request_id }.
export const fetchCounsel = async ({ accountId, conversationId }) => {
  const { data } = await http().get(base(accountId, conversationId));
  logger.info('api.fetched', {
    conversationId,
    count: data?.thread?.messages?.length || 0,
  });
  return data;
};

// Appends the agent's line and enqueues the AI turn. The assistant reply is not
// in this response; it arrives later over ActionCable.
export const createCounselMessage = async ({
  accountId,
  conversationId,
  content,
  mode,
  replyToMessageId,
}) => {
  const payload = { content, mode };
  if (replyToMessageId) payload.reply_to_message_id = replyToMessageId;
  const { data } = await http().post(
    `${base(accountId, conversationId)}/messages`,
    payload
  );
  logger.info('api.messageCreated', { conversationId, mode });
  return data;
};

// Prune a human/assistant line from the counsel thread (and from later prompts).
export const deleteCounselMessage = async ({
  accountId,
  conversationId,
  messageId,
}) => {
  const { data } = await http().delete(
    `${base(accountId, conversationId)}/messages/${messageId}`
  );
  logger.info('api.messageDeleted', { conversationId, messageId });
  return data;
};

// Replays the last question after a failed turn. The failed notice is removed
// server-side, so the agent never has to retype and the thread stays clean.
export const retryCounselTurn = async ({ accountId, conversationId }) => {
  const { data } = await http().post(
    `${base(accountId, conversationId)}/retry`
  );
  logger.info('api.turnRetried', { conversationId });
  return data;
};

// Best-effort stop for the running turn. The flag is read by the host job and
// by the ai-agent service between tool rounds, so the turn ends without writing
// an answer; side effects a tool already performed stay.
export const abortCounselTurn = async ({
  accountId,
  conversationId,
  requestId,
}) => {
  const { data } = await http().post(
    `${base(accountId, conversationId)}/abort`,
    {
      request_id: requestId,
    }
  );
  logger.info('api.turnAborted', { conversationId, requestId });
  return data;
};

export const clearCounselThread = async ({ accountId, conversationId }) => {
  const { data } = await http().post(
    `${base(accountId, conversationId)}/clear`
  );
  logger.info('api.threadCleared', { conversationId });
  return data;
};

// Conversation-level switch shared with the counsel stop/resume tools.
export const updateCounselAutoReply = async ({
  accountId,
  conversationId,
  enabled,
}) => {
  const { data } = await http().patch(
    `${base(accountId, conversationId)}/auto_reply`,
    { enabled }
  );
  logger.info('api.autoReplyUpdated', { conversationId, enabled });
  return data;
};
